import {
  Button,
  CardContent,
  Grid,
  TextField,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import ArrowBackSharpIcon from "@mui/icons-material/ArrowBackSharp";
import React, { useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HOST_URL from "../../../utils/Url";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import ToastApp from "../../../ToastApp";

type Props = {};

const CountryMasterAdd = (props: Props) => {
  let navigate = useNavigate();
  const { t } = useTranslation();
  const requiredFields = ['country_name'];
  const [toaster, setToaster] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const validationSchema = Yup.object({
    country_name: Yup.string()
      .test(
        'required',
        t('text.EnterCountryName'),
        function (value: any) {
          return value && value.trim() !== '';
        }
      )
      .max(20, t('text.countryNameMaxLength'))
      .matches(/^[a-zA-Z\s\-]+$/, t('text.invalidCountryName')) // Only letters, spaces, and hyphens
      .test(
        'no-special-chars-start-end',
        t('text.noSpecialCharsStartEnd'),
        function (value: any) {
          if (!value) return true;
          // Check if starts or ends with special characters (excluding spaces)
          return !/^[^a-zA-Z\s]|[^a-zA-Z\s]$/.test(value);
        }
      )
      .test(
        'no-multiple-special-chars',
        t('text.noMultipleSpecialChars'),
        function (value: any) {
          if (!value) return true;
          // Prevent multiple consecutive special characters
          return !/[\-\s]{2,}/.test(value);
        }
      ),

    country_shortname: Yup.string()
      .max(10, t('text.countryShortNameMaxLength'))
      .matches(/^[a-zA-Z\-]*$/, t('text.invalidCountryShortName')) // Only letters and hyphens
      .test(
        'no-special-chars-start-end-short',
        t('text.noSpecialCharsStartEndShort'),
        function (value: any) {
          if (!value) return true;
          // Check if starts or ends with special characters
          return !/^[^a-zA-Z]|[^a-zA-Z]$/.test(value);
        }
      ),
  });


  // Function to sanitize input by removing unwanted characters
  const sanitizeInput = (value: string, fieldName: string) => {
    if (!value) return value;

    let sanitized = value;

    switch (fieldName) {
      case 'country_name':
        // Allow only letters, spaces, and hyphens
        sanitized = value.replace(/[^a-zA-Z\s\-]/g, '');
        // Remove multiple consecutive special characters
        sanitized = sanitized.replace(/([\-\s]){2,}/g, '$1');
        // Trim special characters from start and end
        sanitized = sanitized.replace(/^[^a-zA-Z\s]+|[^a-zA-Z\s]+$/g, '');
        break;
      case 'country_shortname':
        // Allow only letters and hyphens
        sanitized = value.replace(/[^a-zA-Z\-]/g, '');
        // Trim special characters from start and end
        sanitized = sanitized.replace(/^[^a-zA-Z]+|[^a-zA-Z]+$/g, '');
        break;
      default:
        // Default sanitization for other fields
        sanitized = value.replace(/[<>{}[\]\\]/g, '');
    }

    return sanitized;
  };

  // Custom onChange handler to sanitize input in real-time
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value, name);

    // Only update if the value changed after sanitization
    if (value !== sanitizedValue) {
      formik.setFieldValue(name, sanitizedValue);
    } else {
      formik.handleChange(e);
    }
  };

  const formik = useFormik({
    initialValues: {
      country_id: -1,
      country_shortname: "",
      country_name: "",
      user_id: "",
      inst_ID: 0,
      allowedto: ""
    },
    validationSchema: validationSchema,

    // Update the onSubmit function to include sanitization
    onSubmit: async (values) => {
      // Additional server-side validation before submission
      const sanitizedValues = {
        ...values,
        country_name: sanitizeInput(values.country_name, 'country_name'),
        country_shortname: sanitizeInput(values.country_shortname, 'country_shortname')
      };

      // Clear any existing timeout
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }

      // If already submitting, show message and return
      if (isSubmitting) {
        toast.info(t("text.requestInProgress"));
        return;
      }

      setIsSubmitting(true);

      try {
        const response = await axios.post(
          HOST_URL.HOST_URL2 + `Country/AddUpdateCountryMaster`,
          sanitizedValues // Use sanitized values
        );

        if (response.data.isSuccess) {
          setToaster(false);
          toast.success(response.data.mesg);
          navigate('/master/CountryMaster');
        } else {
          setToaster(true);
          toast.error(response.data.mesg);
        }
      } catch (error: any) {
        if (error.response && error.response.status === 429) {
          toast.error(t("text.tooManyRequests"));
        } else {
          toast.error(t("text.somethingWentWrong"));
        }
        setToaster(true);
      } finally {
        // Set a timeout to reset isSubmitting after 2 seconds
        submitTimeoutRef.current = setTimeout(() => {
          setIsSubmitting(false);
        }, 2000);
      }
    }

  });

  const back = useNavigate();

  // Clean up timeout on component unmount
  React.useEffect(() => {
    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div>
      <div
        style={{
          padding: "-5px 5px",
          backgroundColor: "#ffffff",
          borderRadius: "5px",
          marginTop: "5px",
          border: ".5px solid #FF7722",
        }}
      >
        <CardContent>
          <Typography
            variant="h5"
            textAlign="center"
            style={{ fontSize: "18px", fontWeight: 500 }}
          >
            {t("text.CreateCountryName")}
          </Typography>

          <Grid item sm={4} xs={12}>
            <Typography style={{ marginTop: "-75px" }}>
              <Button
                type="button"
                onClick={() => back(-1)}
                variant="contained"
                style={{
                  marginBottom: 15,
                  marginTop: "45px",
                  backgroundColor: `var(--header-background)`,
                  width: 20,
                }}
                disabled={isSubmitting}
              >
                <ArrowBackSharpIcon />
              </Button>
            </Typography>
          </Grid>
          <Divider />
          <br />
          <form onSubmit={formik.handleSubmit}>
            {toaster === false ? "" : <ToastApp />}
            <Grid item xs={12} container spacing={2}>
              <Grid lg={6} sm={6} xs={12} item>
                <TextField
                  label={
                    <span>
                      {t("text.EnterCountryName")}{requiredFields.includes('country_name') && (
                        <span style={{ color: formik.values.country_name ? 'green' : 'red' }}>*</span>
                      )}
                    </span>
                  }
                  value={formik.values.country_name}
                  placeholder={t("text.EnterCountryName")}
                  size="small"
                  fullWidth
                  name="country_name"
                  id="country_name"
                  style={{ backgroundColor: "white" }}
                  onChange={handleInputChange} // Use custom handler
                  onBlur={formik.handleBlur}
                  disabled={isSubmitting}
                  inputProps={{
                    maxLength: 20, // Reduced to match validation
                    pattern: "[a-zA-Z\\s\\-]*" // HTML5 validation pattern
                  }}
                  error={formik.touched.country_name && Boolean(formik.errors.country_name)}
                />

                {formik.touched.country_name && formik.errors.country_name ? (
                  <div style={{ color: "red", margin: "5px" }}>{formik.errors.country_name}</div>
                ) : null}
              </Grid>

              <Grid lg={6} sm={6} xs={12} item>
           
<TextField
  label={t("text.EnterCountryShortName")}
  value={formik.values.country_shortname}
  placeholder={t("text.EnterCountryShortName")}
  size="small"
  fullWidth
  name="country_shortname"
  id="country_shortname"
  style={{ backgroundColor: "white" }}
  onChange={handleInputChange} // Use custom handler
  onBlur={formik.handleBlur}
  disabled={isSubmitting}
  inputProps={{ 
    maxLength: 10,
    pattern: "[a-zA-Z\\-]*" // HTML5 validation pattern
  }}
  error={formik.touched.country_shortname && Boolean(formik.errors.country_shortname)}
/>

              </Grid>

              <Grid item lg={6} sm={6} xs={12}>
                <Grid>
                  <Button
                    type="submit"
                    fullWidth
                    style={{
                      backgroundColor: `var(--header-background)`,
                      color: "white",
                      marginTop: "10px",
                    }}
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {isSubmitting ? t("text.submitting") : t("text.save")}
                  </Button>
                </Grid>
              </Grid>
              <Grid item lg={6} sm={6} xs={12}>
                <Button
                  type="reset"
                  fullWidth
                  style={{
                    backgroundColor: "#F43F5E",
                    color: "white",
                    marginTop: "10px",
                  }}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    formik.resetForm();
                  }}
                  disabled={isSubmitting}
                >
                  {t("text.reset")}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </div>
    </div>
  );
};

export default CountryMasterAdd;